import { useEffect, useState } from "react";

import { formatChinaDateTime } from "../time";
import {
  approveLawyerVerification,
  getLawyerVerification,
  getLawyerVerificationMaterial,
  listLawyerVerifications,
  rejectLawyerVerification,
  type LawyerVerificationDetail,
  type LawyerVerificationListItem,
  type LawyerVerificationStatus,
} from "./api";

const PAGE_SIZE = 10;

const STATUS_LABELS: Record<LawyerVerificationStatus, string> = {
  NOT_SUBMITTED: "未提交",
  PENDING: "待审核",
  APPROVED: "已通过",
  REJECTED: "已驳回",
};

const MATERIAL_LABELS: Record<string, string> = {
  IDENTITY_FRONT: "身份证人像面",
  IDENTITY_BACK: "身份证国徽面",
  PRACTICE_CERTIFICATE: "律师执业证",
};


export function LawyerIdentityReviews() {
  const [items, setItems] = useState<LawyerVerificationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [status, setStatus] = useState<LawyerVerificationStatus | "">("PENDING");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<LawyerVerificationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listLawyerVerifications({
        query,
        status,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "审核列表加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [page, query, status]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  async function openDetail(id: string) {
    setDetailLoading(true);
    setError("");
    try {
      setDetail(await getLawyerVerification(id));
      setRejecting(false);
      setRejectionReason("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "实名认证详情加载失败");
    } finally {
      setDetailLoading(false);
    }
  }

  async function previewMaterial(url: string) {
    try {
      const blob = await getLawyerVerificationMaterial(url);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "认证材料加载失败");
    }
  }

  async function approve() {
    if (!detail || submitting || !window.confirm("确认通过该律师的实名认证？")) return;
    setSubmitting(true);
    try {
      await approveLawyerVerification(detail.id);
      setDetail(null);
      await reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "审核提交失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function reject() {
    if (!detail || submitting || !rejectionReason.trim()) return;
    setSubmitting(true);
    try {
      await rejectLawyerVerification(detail.id, rejectionReason.trim());
      setDetail(null);
      await reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "驳回提交失败");
    } finally {
      setSubmitting(false);
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="platform-module platform-lawyer-reviews">
      <form
        className="platform-list-toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQuery(draftQuery.trim());
        }}
      >
        <input
          className="platform-list-search"
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
          placeholder="搜索律师姓名、手机号、身份证号或执业证号"
          aria-label="搜索实名认证申请"
        />
        <select
          value={status}
          aria-label="审核状态"
          onChange={(event) => {
            setPage(1);
            setStatus(event.target.value as LawyerVerificationStatus | "");
          }}
        >
          <option value="PENDING">待审核</option>
          <option value="APPROVED">已通过</option>
          <option value="REJECTED">已驳回</option>
          <option value="">全部状态</option>
        </select>
        <button className="platform-primary-button" type="submit">查询</button>
        <button
          type="button"
          onClick={() => {
            setDraftQuery("");
            setQuery("");
            setStatus("PENDING");
            setPage(1);
          }}
        >重置</button>
      </form>

      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="platform-table-wrap">
        <table className="platform-account-table">
          <thead><tr>
            <th>律师</th><th>注册手机号</th><th>所在律所</th><th>提交时间</th><th>审核状态</th><th>审核人</th><th>操作</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="platform-table-state">正在加载…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="platform-table-state">暂无符合条件的实名认证申请</td></tr>}
            {!loading && items.map((item) => <tr key={item.id}>
              <td><strong>{item.lawyer_name}</strong><small>{item.lawyer_number}</small></td>
              <td>{item.mobile}</td>
              <td>{item.law_firm_name ?? "—"}</td>
              <td>{formatChinaDateTime(item.submitted_at)}</td>
              <td><span className={`platform-status is-${item.status.toLowerCase()}`}>{STATUS_LABELS[item.status]}</span></td>
              <td>—</td>
              <td><button className="platform-link-button" type="button" onClick={() => void openDetail(item.id)}>
                {item.status === "PENDING" ? "审核" : "查看"}
              </button></td>
            </tr>)}
          </tbody>
        </table>
      </div>
      <footer className="platform-pagination">
        <span>共 {total} 条审核记录，每页 {PAGE_SIZE} 条</span>
        <div>
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</button>
          <span>{page} / {pageCount}</span>
          <button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>下一页</button>
        </div>
      </footer>

      {(detail || detailLoading) && <div className="platform-modal-layer">
        <section className="platform-modal platform-review-detail" role="dialog" aria-modal="true" aria-label="律师实名认证审核">
          {detailLoading && <p>正在加载审核资料…</p>}
          {detail && <>
            <header><div><h2>{detail.lawyer_name}的实名认证</h2><p>{detail.lawyer_number} · {detail.mobile}</p></div><button type="button" onClick={() => setDetail(null)} aria-label="关闭">×</button></header>
            <dl className="platform-review-fields">
              <div><dt>所在律所</dt><dd>{detail.law_firm_name ?? "—"}</dd></div>
              <div><dt>身份证号</dt><dd>{detail.identity_number}</dd></div>
              <div><dt>律师执业证号</dt><dd>{detail.practice_certificate_number}</dd></div>
              <div><dt>提交时间</dt><dd>{formatChinaDateTime(detail.submitted_at)}</dd></div>
            </dl>
            <h3>认证材料</h3>
            <div className="platform-review-materials">{detail.materials.map((material) => <button type="button" key={material.id} onClick={() => void previewMaterial(material.review_url)}>
              <span>{MATERIAL_LABELS[material.kind] ?? material.name}</span><small>查看水印副本</small>
            </button>)}</div>
            <h3>审核记录</h3>
            {detail.history.length === 0 ? <p className="platform-muted">暂无审核记录</p> : <ul className="platform-review-history">{detail.history.map((item, index) => <li key={`${item.created_at}-${index}`}><strong>{item.action === "APPROVE" ? "认证审核通过" : "认证审核驳回"}</strong><span>{formatChinaDateTime(item.created_at)} · {item.operator_name}</span>{item.reason && <p>{item.reason}</p>}</li>)}</ul>}
            {detail.status === "PENDING" && <footer className="platform-review-actions">
              {rejecting && <textarea value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} placeholder="请填写明确的驳回原因" aria-label="驳回原因" maxLength={500} />}
              <button type="button" onClick={() => setRejecting(true)}>驳回申请</button>
              {rejecting && <button type="button" disabled={!rejectionReason.trim() || submitting} onClick={() => void reject()}>确认驳回</button>}
              <button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void approve()}>审核通过</button>
            </footer>}
          </>}
        </section>
      </div>}

      {previewUrl && <div className="platform-modal-layer"><section className="platform-modal platform-material-preview" role="dialog" aria-modal="true" aria-label="认证材料水印副本"><header><h2>认证材料水印副本</h2><button type="button" onClick={() => setPreviewUrl(null)} aria-label="关闭">×</button></header><img src={previewUrl} alt="认证材料水印副本" /></section></div>}
    </section>
  );
}
